// Molinetes — cadastro do equipamento de medição de velocidade + calibração.
//
// Extraído de EstacoesMapa.tsx em 2026-08-06 (movimento puro, sem reescrita).
// Bloco totalmente independente do resto do módulo: não conhece estação, série
// nem curva-chave — só o próprio equipamento e os destinatários do aviso de
// vencimento da calibração.
import { useEffect, useRef, useState } from 'react';
import {
  App as AntApp, Button, Modal, Input, Select, InputNumber, Table, Tag,
  Space, DatePicker, Empty, Popconfirm, Typography, Tooltip, Row, Col,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { api, Molinete, FaixaCal, DestAlerta } from './shared';

const { Text } = Typography;

// Tag de status do vencimento da calibração do molinete (verde/laranja/vermelho).
function TagCalibracao({ m }: { m: Molinete }) {
  if (!m.data_calibracao) return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
  const d = m.dias_para_vencer;
  const validade = m.validade_calibracao ? dayjs(m.validade_calibracao).format('DD/MM/YYYY') : '—';
  let color = 'green', txt = `Válida até ${validade}`;
  if (d != null && d <= 0) { color = 'red'; txt = `Vencida (${validade})`; }
  else if (d != null && d <= 30) { color = 'orange'; txt = `Vence em ${d}d (${validade})`; }
  return <Tag color={color}>{txt}</Tag>;
}

export function ModalMolinetes({ onClose }: { onClose: () => void }) {
  const { message } = AntApp.useApp();
  const [lista, setLista] = useState<Molinete[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<{ open: boolean; editando: Molinete | null }>({ open: false, editando: null });
  const [destOpen, setDestOpen] = useState(false);

  async function carregar() {
    setLoading(true);
    try { const d = await api('op_molinete_listar'); setLista(Array.isArray(d?.molinetes) ? d.molinetes : []); }
    catch (e: any) { message.error('Erro ao listar: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  async function excluir(m: Molinete) {
    try { await api('op_molinete_excluir', { id: m.id }); message.success('Molinete excluído.'); carregar(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }

  const cols = [
    { title: 'Código', dataIndex: 'codigo', render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Equipamento', key: 'eq', render: (_: any, m: Molinete) => [m.marca, m.modelo].filter(Boolean).join(' ') || '—' },
    { title: 'Hélice', dataIndex: 'helice', render: (v: string) => v || '—' },
    { title: 'Faixas', key: 'fx', render: (_: any, m: Molinete) => (m.faixas?.length || 0) },
    { title: 'Calibração', key: 'calib', render: (_: any, m: Molinete) => <TagCalibracao m={m} /> },
    { title: '', dataIndex: 'ativo', render: (a: boolean) => a ? null : <Tag>inativo</Tag> },
    { title: '', key: 'acoes', render: (_: any, m: Molinete) => (
      <Space>
        <Tooltip title="Editar"><Button size="small" icon={<EditOutlined />} onClick={() => setForm({ open: true, editando: m })} /></Tooltip>
        <Popconfirm title="Excluir molinete?" onConfirm={() => excluir(m)} okText="Excluir" cancelText="Cancelar">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    ) },
  ];

  return (
    <Modal open width={760} title="🌀 Molinetes (current meters)" onCancel={onClose} footer={null}>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Equipamentos de medição de velocidade. Cada um guarda a reta de calibração
          v = a·n + b (n = rotações ÷ tempo), usada pelo app de campo pra calcular a vazão.
        </Text>
        <Space>
          <Button size="small" onClick={() => setDestOpen(true)}>🔔 Destinatários dos avisos</Button>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setForm({ open: true, editando: null })}>Novo</Button>
        </Space>
      </div>
      <Table rowKey="id" size="small" loading={loading} columns={cols as any} dataSource={lista}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description="Nenhum molinete cadastrado" /> }} />
      {form.open && <ModalMolineteForm editando={form.editando}
        onClose={() => setForm({ open: false, editando: null })}
        onSalvo={() => { setForm({ open: false, editando: null }); carregar(); }} />}
      {destOpen && <ModalDestinatariosCalib onClose={() => setDestOpen(false)} />}
    </Modal>
  );
}

// Destinatários dos avisos de vencimento da calibração — cadastráveis na plataforma.
function ModalDestinatariosCalib({ onClose }: { onClose: () => void }) {
  const { message } = AntApp.useApp();
  const [lista, setLista] = useState<DestAlerta[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [nome, setNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    setLoading(true);
    try { const d = await api('op_molinete_dest_listar'); setLista(Array.isArray(d?.destinatarios) ? d.destinatarios : []); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, []);

  async function adicionar() {
    if (!email.trim()) { message.warning('Informe o e-mail'); return; }
    setSalvando(true);
    try { await api('op_molinete_dest_salvar', { email: email.trim(), nome: nome.trim() || null, ativo: true });
      setEmail(''); setNome(''); message.success('Destinatário salvo.'); carregar(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }
  async function toggle(d: DestAlerta) {
    try { await api('op_molinete_dest_salvar', { email: d.email, nome: d.nome, ativo: !d.ativo }); carregar(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  async function remover(d: DestAlerta) {
    try { await api('op_molinete_dest_excluir', { id: d.id }); carregar(); }
    catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }

  return (
    <Modal open width={560} title="🔔 Destinatários dos avisos de calibração" onCancel={onClose} footer={null}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Quem recebe (sino + e-mail) os avisos de calibração dos molinetes — 30, 15, 7, 3 e 1 dias antes, além de um aviso no vencimento.
      </Text>
      <div style={{ display: 'flex', gap: 6, margin: '12px 0' }}>
        <Input placeholder="e-mail" value={email} onChange={e => setEmail(e.target.value)} onPressEnter={adicionar} style={{ flex: 2 }} />
        <Input placeholder="nome (opcional)" value={nome} onChange={e => setNome(e.target.value)} onPressEnter={adicionar} style={{ flex: 2 }} />
        <Button type="primary" icon={<PlusOutlined />} loading={salvando} onClick={adicionar}>Add</Button>
      </div>
      <Table rowKey="id" size="small" loading={loading} pagination={false}
        locale={{ emptyText: <Empty description="Ninguém cadastrado" /> }}
        dataSource={lista}
        columns={[
          { title: 'E-mail', dataIndex: 'email' },
          { title: 'Nome', dataIndex: 'nome', render: (v: string) => v || <Text type="secondary">—</Text> },
          { title: 'Ativo', dataIndex: 'ativo', width: 70, render: (a: boolean, d: DestAlerta) =>
            <Select size="small" value={a ? 'sim' : 'nao'} onChange={() => toggle(d)} style={{ width: 70 }}
              options={[{ value: 'sim', label: 'Sim' }, { value: 'nao', label: 'Não' }]} /> },
          { title: '', key: 'x', width: 40, render: (_: any, d: DestAlerta) =>
            <Popconfirm title="Remover?" onConfirm={() => remover(d)} okText="Remover" cancelText="Cancelar">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm> },
        ] as any} />
    </Modal>
  );
}

function ModalMolineteForm({ editando, onClose, onSalvo }: {
  editando: Molinete | null; onClose: () => void; onSalvo: () => void;
}) {
  const { message } = AntApp.useApp();
  const [salvando, setSalvando] = useState(false);
  const [codigo, setCodigo] = useState(editando?.codigo || '');
  const [marca, setMarca] = useState(editando?.marca || '');
  const [modelo, setModelo] = useState(editando?.modelo || '');
  const [helice, setHelice] = useState(editando?.helice || '');
  const [serie, setSerie] = useState(editando?.numero_serie || '');
  const [obs, setObs] = useState(editando?.observacao || '');
  const [ativo, setAtivo] = useState<boolean>(editando?.ativo ?? true);
  const [numCalib, setNumCalib] = useState(editando?.numero_calibracao || '');
  const [dataCalib, setDataCalib] = useState<Dayjs | null>(editando?.data_calibracao ? dayjs(editando.data_calibracao) : null);
  const [laudoKey, setLaudoKey] = useState<string | null>(editando?.laudo_s3_key || null);
  const [laudoNome, setLaudoNome] = useState<string | null>(editando?.laudo_nome || null);
  const [subindo, setSubindo] = useState(false);
  const laudoInputRef = useRef<HTMLInputElement>(null);
  const [faixas, setFaixas] = useState<FaixaCal[]>(
    editando?.faixas?.length ? editando.faixas : [{ n_min: 0, n_max: null, a: null, b: null }]
  );

  async function enviarLaudo(file: File) {
    setSubindo(true);
    try {
      const d = await api('op_molinete_laudo_upload_url', { nome: file.name, content_type: file.type || 'application/pdf' });
      await axios.put(d.upload_url, file, { headers: { 'Content-Type': file.type || 'application/pdf' }, timeout: 120000 });
      setLaudoKey(d.s3_key); setLaudoNome(file.name);
      message.success('Laudo anexado.');
    } catch (e: any) { message.error('Falha no upload: ' + (e.response?.data?.error || e.message)); }
    finally { setSubindo(false); }
  }
  async function baixarLaudo() {
    if (!laudoKey) return;
    try { const d = await api('op_molinete_laudo_download_url', { s3_key: laudoKey }); window.open(d.url, '_blank', 'noopener'); }
    catch (e: any) { message.error('Falha ao abrir: ' + (e.response?.data?.error || e.message)); }
  }

  function setFx(i: number, patch: Partial<FaixaCal>) {
    setFaixas((f) => f.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  }
  function addFaixa() { setFaixas((f) => [...f, { n_min: null, n_max: null, a: null, b: null }]); }
  function rmFaixa(i: number) { setFaixas((f) => f.filter((_, idx) => idx !== i)); }

  async function salvar() {
    const fx = faixas.filter(f => f.a != null && f.b != null);
    if (!fx.length) { message.error('Informe ao menos uma faixa com a e b.'); return; }
    setSalvando(true);
    try {
      const payload: any = {
        codigo, marca, modelo, helice, numero_serie: serie, observacao: obs, ativo, faixas: fx,
        numero_calibracao: numCalib,
        data_calibracao: dataCalib ? dataCalib.format('YYYY-MM-DD') : '',
        laudo_s3_key: laudoKey || '', laudo_nome: laudoNome || '',
      };
      await api(editando ? 'op_molinete_atualizar' : 'op_molinete_criar', editando ? { id: editando.id, ...payload } : payload);
      message.success('Molinete salvo.');
      onSalvo();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setSalvando(false); }
  }

  return (
    <Modal open title={editando ? 'Editar molinete' : 'Novo molinete'} onCancel={onClose}
      onOk={salvar} confirmLoading={salvando} okText="Salvar" width={620}>
      <Row gutter={8}>
        <Col span={8}><Text style={{ fontSize: 12 }}>Código / patrimônio</Text><Input value={codigo} onChange={e => setCodigo(e.target.value)} /></Col>
        <Col span={8}><Text style={{ fontSize: 12 }}>Marca</Text><Input value={marca} onChange={e => setMarca(e.target.value)} /></Col>
        <Col span={8}><Text style={{ fontSize: 12 }}>Modelo</Text><Input value={modelo} onChange={e => setModelo(e.target.value)} /></Col>
      </Row>
      <Row gutter={8} style={{ marginTop: 8 }}>
        <Col span={12}><Text style={{ fontSize: 12 }}>Hélice</Text><Input value={helice} onChange={e => setHelice(e.target.value)} placeholder="ex: nº 1 / passo 0,25 m" /></Col>
        <Col span={12}><Text style={{ fontSize: 12 }}>Nº de série</Text><Input value={serie} onChange={e => setSerie(e.target.value)} /></Col>
      </Row>

      <div style={{ marginTop: 14, marginBottom: 6 }}>
        <Text strong>Aferição / calibração do equipamento</Text>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>vencimento = 1 ano após a data. O sistema avisa os destinatários 30 dias antes e no vencimento.</Text>
      </div>
      <Row gutter={8} align="bottom">
        <Col span={8}><Text style={{ fontSize: 12 }}>Nº da calibração</Text><Input value={numCalib} onChange={e => setNumCalib(e.target.value)} placeholder="nº do certificado" /></Col>
        <Col span={8}><Text style={{ fontSize: 12, display: 'block' }}>Data da calibração</Text>
          <DatePicker value={dataCalib} onChange={setDataCalib} format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="dd/mm/aaaa" />
        </Col>
        <Col span={8}><Text style={{ fontSize: 12, display: 'block' }}>Laudo / certificado</Text>
          <input ref={laudoInputRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) enviarLaudo(f); e.currentTarget.value = ''; }} />
          {laudoKey ? (
            <Space size={4}>
              <Button size="small" icon={<EyeOutlined />} onClick={baixarLaudo}>{(laudoNome || 'laudo').slice(0, 16)}</Button>
              <Tooltip title="Trocar"><Button size="small" icon={<UploadOutlined />} loading={subindo} onClick={() => laudoInputRef.current?.click()} /></Tooltip>
              <Tooltip title="Remover"><Button size="small" danger icon={<DeleteOutlined />} onClick={() => { setLaudoKey(null); setLaudoNome(null); }} /></Tooltip>
            </Space>
          ) : (
            <Button size="small" icon={<UploadOutlined />} loading={subindo} onClick={() => laudoInputRef.current?.click()} style={{ width: '100%' }}>Anexar</Button>
          )}
        </Col>
      </Row>

      <div style={{ marginTop: 14, marginBottom: 6 }}>
        <Text strong>Calibração — v = a·n + b</Text>
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>n = rotações ÷ tempo (rps). Uma linha por faixa de rotação.</Text>
      </div>
      <table style={{ width: '100%', fontSize: 12 }}>
        <thead>
          <tr style={{ color: '#888' }}>
            <th style={{ textAlign: 'left', fontWeight: 400 }}>n de (rps)</th>
            <th style={{ textAlign: 'left', fontWeight: 400 }}>n até (vazio = ∞)</th>
            <th style={{ textAlign: 'left', fontWeight: 400 }}>a</th>
            <th style={{ textAlign: 'left', fontWeight: 400 }}>b</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {faixas.map((f, i) => (
            <tr key={i}>
              <td><InputNumber decimalSeparator="," size="small" style={{ width: '100%' }} value={f.n_min ?? undefined} step={0.1} onChange={(v) => setFx(i, { n_min: v as number })} /></td>
              <td><InputNumber decimalSeparator="," size="small" style={{ width: '100%' }} value={f.n_max ?? undefined} step={0.1} onChange={(v) => setFx(i, { n_max: v as number })} /></td>
              <td><InputNumber decimalSeparator="," size="small" style={{ width: '100%' }} value={f.a ?? undefined} step={0.0001} onChange={(v) => setFx(i, { a: v as number })} /></td>
              <td><InputNumber decimalSeparator="," size="small" style={{ width: '100%' }} value={f.b ?? undefined} step={0.0001} onChange={(v) => setFx(i, { b: v as number })} /></td>
              <td><Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={faixas.length <= 1} onClick={() => rmFaixa(i)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addFaixa} style={{ marginTop: 6 }}>Adicionar faixa</Button>

      <Row gutter={8} style={{ marginTop: 14 }} align="bottom">
        <Col flex="auto"><Text style={{ fontSize: 12 }}>Observação</Text><Input value={obs} onChange={e => setObs(e.target.value)} /></Col>
        <Col><Text style={{ fontSize: 12, display: 'block' }}>Ativo</Text><Select value={ativo ? 'sim' : 'nao'} onChange={(v) => setAtivo(v === 'sim')} options={[{ value: 'sim', label: 'Sim' }, { value: 'nao', label: 'Não' }]} style={{ width: 90 }} /></Col>
      </Row>
    </Modal>
  );
}
